import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Image, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fullscreen Lightbox Component
const Lightbox = ({ images, initialIndex, onClose, categoryName }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, goToPrevious, goToNext]);

  const currentImage = images[currentIndex];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 bg-black/60 hover:bg-[#D4AF37] text-white p-3 rounded-full transition-all duration-300"
        data-testid="lightbox-close"
      >
        <X size={24} />
      </button>

      {/* Image Counter */}
      <div className="absolute top-4 left-4 z-10 bg-black/60 text-white px-4 py-2 rounded-full">
        <span className="text-[#D4AF37] font-bold">{currentIndex + 1}</span>
        <span className="text-white/60"> / {images.length}</span>
      </div>

      {/* Item Title - Above Image */}
      {currentImage?.itemTitle && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-black/60 text-white px-6 py-3 rounded-lg max-w-xl text-center">
          <h3 className="text-xl font-bold text-white/80">{currentImage.itemTitle}</h3>
        </div>
      )}

      {/* Main Image */}
      <div 
        className="relative w-full h-full flex items-center justify-center p-4 md:p-16 pt-20"
        onClick={(e) => e.stopPropagation()}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={currentImage?.url}
            alt={`${categoryName} - Image ${currentIndex + 1}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="max-w-full max-h-full object-contain"
            data-testid="lightbox-image"
          />
        </AnimatePresence>
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#D4AF37] text-white p-4 rounded-full transition-all duration-300"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#D4AF37] text-white p-4 rounded-full transition-all duration-300"
            data-testid="lightbox-next"
          >
            <ChevronRight size={32} />
          </button>
        </>
      )}

      {/* Thumbnail Strip */}
      {images.length > 1 && images.length <= 20 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 p-2 rounded-lg max-w-[90vw] overflow-x-auto">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
              className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all duration-300 ${
                index === currentIndex 
                  ? 'border-[#D4AF37] scale-110' 
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Keyboard hints */}
      <div className="absolute bottom-4 right-4 text-white/40 text-xs hidden md:block">
        ESC to close • Arrow keys to navigate
      </div>
    </motion.div>
  );
};

// Main Category Carousel - Shows all images from all items
const CategoryCarousel = ({ allImages, onImageClick, categoryName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  // Auto-advance every 4 seconds
  useEffect(() => {
    if (allImages.length <= 1 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % allImages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [allImages.length, isPaused]);
  
  if (allImages.length === 0) {
    return (
      <div className="w-full h-[500px] bg-black/20 flex items-center justify-center border border-white/10 rounded-lg">
        <div className="text-white/30 text-center">
          <Image size={64} className="mx-auto mb-4" />
          <p className="text-lg">No images in this gallery yet</p>
          <p className="text-sm mt-2">Add items via the Admin Dashboard</p>
        </div>
      </div>
    );
  }

  const goToPrevious = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const goToNext = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const handleImageClick = () => {
    if (onImageClick && allImages.length > 0) {
      onImageClick(currentIndex);
    }
  };

  const currentImage = allImages[currentIndex];

  return (
    <div className="space-y-4">
      {/* Item Title Above Carousel */}
      {currentImage?.itemTitle && (
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white/70">{currentImage.itemTitle}</h2>
        </div>
      )}

      {/* Main Carousel */}
      <div 
        className="relative w-full h-[500px] bg-black/20 flex items-center justify-center overflow-hidden group cursor-pointer border border-white/10 rounded-lg"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onClick={handleImageClick}
        data-testid="main-carousel"
      >
        {/* Current Image with fade transition */}
        <AnimatePresence mode="wait">
          <motion.img 
            key={currentIndex}
            src={currentImage?.url} 
            alt={currentImage?.itemTitle || categoryName}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full h-full object-contain"
          />
        </AnimatePresence>

        {/* Zoom indicator on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center pointer-events-none">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60 p-4 rounded-full">
            <ZoomIn size={32} className="text-[#D4AF37]" />
          </div>
        </div>
        
        {allImages.length > 1 && (
          <>
            {/* Left Arrow */}
            <button 
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#D4AF37] text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"
              data-testid="carousel-prev"
            >
              <ChevronLeft size={28} />
            </button>
            
            {/* Right Arrow */}
            <button 
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#D4AF37] text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"
              data-testid="carousel-next"
            >
              <ChevronRight size={28} />
            </button>
            
            {/* Image Counter & Auto-rotate indicator */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              <div className={`w-2.5 h-2.5 rounded-full ${isPaused ? 'bg-white/50' : 'bg-[#D4AF37] animate-pulse'}`} />
              <span className="bg-black/60 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                {currentIndex + 1} / {allImages.length}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Thumbnail Strip Below Carousel */}
      {allImages.length > 1 && (
        <div className="relative">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-[#D4AF37]/50 scrollbar-track-black/20">
            {allImages.map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                  index === currentIndex 
                    ? 'border-[#D4AF37] scale-105 shadow-[0_0_10px_rgba(212,175,55,0.5)]' 
                    : 'border-white/10 opacity-60 hover:opacity-100 hover:border-white/30'
                }`}
                data-testid={`thumbnail-${index}`}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const GalleryCategory = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const categoryNames = {
    'lightboxes-preset': 'Lightboxes - Preset Sizes',
    'custom-signage': 'Custom Signage',
    'acrylic-fabrication': 'Acrylic Fabrication',
    'point-of-sale': 'Point of Sale Displays',
    'vehicle-graphics': 'Vehicle Graphics',
    'dimensional-letters': 'Dimensional Letters',
    'event-displays': 'Event Displays',
    'retail-fixtures': 'Retail Fixtures',
  };

  // Collect all images from all items with their titles
  const allImages = useMemo(() => {
    const images = [];
    items.forEach(item => {
      // Get images array or fallback to single image
      const itemImages = item.images && item.images.length > 0 
        ? item.images 
        : (item.image ? [item.image] : []);
      
      // Filter out empty images and add with item title
      itemImages
        .filter(img => img && img.trim() !== '')
        .forEach(img => {
          images.push({
            url: img,
            itemTitle: item.title || item.name,
            itemId: item.id
          });
        });
    });
    return images;
  }, [items]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const catResponse = await axios.get(`${API}/gallery-categories/${categoryId}`);
      setCategory(catResponse.data);
      
      const itemsResponse = await axios.get(`${API}/gallery-items?category_id=${categoryId}`);
      setItems(itemsResponse.data);
    } catch (error) {
      setCategory({ id: categoryId, name: categoryNames[categoryId] || 'Gallery' });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 px-6 flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">Loading gallery...</p>
        </div>
      </div>
    );
  }

  const categoryName = category?.name || categoryNames[categoryId] || 'Gallery';

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            images={allImages}
            initialIndex={lightboxIndex}
            onClose={closeLightbox}
            categoryName={categoryName}
          />
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-6xl mx-auto"
      >
        <Button
          onClick={() => navigate('/gallery')}
          variant="ghost"
          className="mb-6 text-white/70 hover:text-white"
        >
          <ArrowLeft className="mr-2" size={20} /> Back to Gallery
        </Button>

        <h1 className="text-5xl md:text-6xl font-black mb-4" data-testid="gallery-category-title">{categoryName}</h1>
        <p className="text-xl text-white/70 mb-8">
          {allImages.length > 0 
            ? `${allImages.length} image${allImages.length !== 1 ? 's' : ''} from ${items.length} project${items.length !== 1 ? 's' : ''}`
            : 'No images available yet'
          }
        </p>

        {/* Main Category Carousel */}
        <CategoryCarousel 
          allImages={allImages}
          onImageClick={openLightbox}
          categoryName={categoryName}
        />

        {/* Items List Below */}
        {items.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6 text-[#D4AF37]">Projects in this Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item, index) => {
                const itemImages = item.images && item.images.length > 0 
                  ? item.images.filter(img => img && img.trim() !== '')
                  : (item.image ? [item.image] : []);
                
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-[#D4AF37]/50 transition-all duration-300"
                    data-testid={`project-item-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      {itemImages.length > 0 && (
                        <img 
                          src={itemImages[0]} 
                          alt={item.title} 
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white mb-1 truncate">{item.title || item.name}</h3>
                        <p className="text-white/60 text-sm line-clamp-2">{item.description}</p>
                        {itemImages.length > 0 && (
                          <p className="text-[#D4AF37] text-xs mt-2">
                            {itemImages.length} image{itemImages.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Coming Soon Notice */}
        <div className="mt-12 p-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg text-center">
          <p className="text-[#D4AF37] font-bold mb-1">More Items Coming Soon</p>
          <p className="text-white/60 text-sm">We're continuously adding new work to this gallery. Check back regularly for updates!</p>
        </div>
      </motion.div>
    </div>
  );
};

export default GalleryCategory;
